import { useId, useState } from 'react';
import type { PromoAssessment } from '@mypoz/shared';
import { voucherTransport } from '@mypoz/shared';
import { useTranslation } from '@mypoz/i18n';

import { actionLinkClass, buttonClass } from '@/lib/styles.js';
import { useFormat } from '@/lib/format.js';

export interface PromoCodeFieldProps {
  /** The code on the draft: empty until one is applied. */
  appliedCode: string;
  /** Checks a code against the customer's vouchers and the chosen channel (`assessPromoCode`). */
  assess: (code: string) => PromoAssessment;
  onApply: (code: string) => void;
  onRemove: () => void;
  /** Still fetching the voucher list: nothing can be checked yet. */
  vouchersLoading: boolean;
  /** Set when an applied code was dropped because it does not fit a newly chosen channel. */
  cleared?: boolean;
}

/**
 * The promo code on Step 3 of New order (MYP-95).
 *
 * There is no billing weight yet, so the vendor's `OrderCodenoCheck` cannot run honestly here.
 * What can be checked is the customer's own voucher list: a code on it that has expired, been
 * used, is not open yet, or is for the other transport (or the shopping mall) is refused and never
 * sent. A code that is not on the list, such as a public promo code, is accepted and checked by
 * the vendor when the order is created (decision (a)). Either way no discount amount is shown:
 * the vendor applies it when it prices the order.
 */
export function PromoCodeField({
  appliedCode,
  assess,
  onApply,
  onRemove,
  vouchersLoading,
  cleared = false,
}: PromoCodeFieldProps) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const inputId = useId();
  const problemId = useId();
  const [input, setInput] = useState('');
  const [rejection, setRejection] = useState<Extract<PromoAssessment, { kind: 'rejected' }> | null>(
    null,
  );

  const apply = () => {
    const result = assess(input);
    if (result.kind === 'empty') return;
    if (result.kind === 'rejected') {
      setRejection(result);
      return;
    }
    setRejection(null);
    setInput('');
    onApply(result.code);
  };

  if (appliedCode !== '') {
    const current = assess(appliedCode);
    const coupon = current.kind === 'voucher' ? current.coupon : undefined;
    return (
      <div>
        <p className="mb-1.5 text-sm text-muted">{t('orders.newOrder.delivery.promo.label')}</p>
        <div className="flex flex-wrap items-start justify-between gap-3 rounded-md border border-line bg-surface-alt px-3 py-2">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-ink">
              {t('orders.newOrder.delivery.promo.applied', { code: appliedCode })}
            </p>
            <p className="mt-0.5 text-sm text-muted">
              {coupon
                ? coupon.minSpendCents
                  ? t('orders.newOrder.delivery.promo.voucherMinSpend', {
                      amount: fmt.money(coupon.valueCents, coupon.currency),
                      min: fmt.money(coupon.minSpendCents, coupon.currency),
                    })
                  : t('orders.newOrder.delivery.promo.voucherOff', {
                      amount: fmt.money(coupon.valueCents, coupon.currency),
                    })
                : t('orders.newOrder.delivery.promo.unlistedNote')}
            </p>
            {coupon ? (
              <p className="mt-0.5 text-sm text-muted">
                {t('orders.newOrder.delivery.promo.pricedNote')}
              </p>
            ) : null}
          </div>
          <button type="button" onClick={onRemove} className={actionLinkClass()}>
            {t('orders.newOrder.delivery.promo.remove')}
          </button>
        </div>
      </div>
    );
  }

  const transport = rejection ? voucherTransport(rejection.coupon) : undefined;
  const reasonKey =
    rejection === null
      ? null
      : rejection.reason === 'wrongTransport'
        ? transport === 'SEA'
          ? 'wrongTransportSea'
          : 'wrongTransportAir'
        : rejection.reason;

  return (
    <div>
      <label htmlFor={inputId} className="mb-1.5 block text-sm text-muted">
        {t('orders.newOrder.delivery.promo.label')}
      </label>
      <div className="flex gap-2">
        <input
          id={inputId}
          type="text"
          autoComplete="off"
          value={input}
          aria-invalid={rejection !== null}
          aria-describedby={rejection ? problemId : undefined}
          onChange={(event) => {
            setInput(event.target.value);
            setRejection(null);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              apply();
            }
          }}
          placeholder={t('orders.newOrder.delivery.promo.placeholder')}
          className="min-h-11 w-full min-w-0 rounded-md border border-line bg-surface px-3 text-sm text-ink transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand lg:min-h-10"
        />
        <button
          type="button"
          onClick={apply}
          disabled={input.trim() === '' || vouchersLoading}
          className={buttonClass(
            'secondary',
            'shrink-0 disabled:cursor-not-allowed disabled:opacity-50',
          )}
        >
          {vouchersLoading
            ? t('orders.newOrder.delivery.promo.checking')
            : t('orders.newOrder.delivery.promo.apply')}
        </button>
      </div>
      {reasonKey ? (
        <p id={problemId} role="alert" className="mt-1.5 text-sm text-danger">
          {t(`orders.newOrder.delivery.promo.reject.${reasonKey}`)}
        </p>
      ) : cleared ? (
        <p role="status" className="mt-1.5 text-sm text-muted">
          {t('orders.newOrder.delivery.promo.cleared')}
        </p>
      ) : null}
    </div>
  );
}
